import Breadcrumbs from '@mui/material/Breadcrumbs';
// import Link from '@mui/material/Link';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { CSSProperties, useMemo } from 'react';
import { useRouter } from '@/hooks';

import { useAtomValue } from 'jotai';
import { useGetStructureNameQuery } from '@/__generated__/graphql';
import { Container } from './styles';
import { titleCaseSentence } from '@/utils';
import * as state from '@/state';
import { useFeatureFlag } from '@/hooks';
import { LinkComposed } from '../LinkComposed';
import { ZoneStatusChanger } from '../../ZonesMangement/ZoneStatusChanger';

type Props = {
  inspectionId?: string;
  assetName?: string;
};

export const Breadcrumb = ({ inspectionId, assetName }: Props) => {
  const selectedStructureId = useAtomValue(state.selectedStructureId);
  const getRecoilStructureName = useAtomValue(
    state.structureNameByStructureId
  );

 
  const recoilStructureName = getRecoilStructureName(
    typeof selectedStructureId === 'string' ? selectedStructureId : undefined
  );
    const selectedZone = useAtomValue(state.selectedZone);

  // gets every path name after inspection/from the router and formats it for the breadcrumb.
  // eg. /inspection/[inspection]/stage-overview/analysis => ['[inspection]', 'Stage Overview', 'Analysis']
  const router = useRouter();
  const isBetaUser = useFeatureFlag('beta-user');
  const paths = router.pathname.split('/'); // the split adds an '' to the beginning of the array (paths[0] === '')
  paths.splice(0, 2);
  const trailingPaths = paths.map((path) => {
    return titleCaseSentence(path.replace('-', ' '));
  });
  const lastTrailingPath = trailingPaths.pop();

  const structureId = inspectionId || selectedStructureId || '';
  const isAtInspectionPage = router.pathname === `/inspection/${structureId}`;

  const { data } = useGetStructureNameQuery({
    variables: {
      structureId,
    },
    fetchPolicy: 'cache-first',
    skip: !!recoilStructureName || !structureId,
  });

  const queryStructureName = useMemo(() => {
    if (data?.structure?.name) {
      return data.structure.name;
    }
    return '';
  }, [data?.structure?.name]);

  const lastPath = useMemo(() => {
    if (lastTrailingPath === 'Asset') {
      return assetName;
    }
    if (lastTrailingPath === structureId) {
      return queryStructureName || recoilStructureName;
    }
    return lastTrailingPath;
  }, [assetName, queryStructureName, lastTrailingPath, recoilStructureName, structureId]);

  const containerStyles: CSSProperties = {
    display: 'flex',
    paddingLeft: '2rem',
    paddingRight: '0.6rem',
    paddingTop: '1rem',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  return (
    <Container style={containerStyles}>
      <Breadcrumbs aria-label="breadcrumb">
        <Link underline="hover" color="inherit" component={LinkComposed} to="/">
          <Typography variant="body1">Platforms</Typography>
        </Link>
        {trailingPaths &&
          trailingPaths.map((trailPath, index) => {
            const path = '/inspection/' + paths.slice(0, index).join('/');
            return (
              <Link
                key={trailPath}
                component={LinkComposed}
                underline="hover"
                color="inherit"
                to={path}
              >
                <Typography variant="body1">
                  {trailPath === structureId
                    ? queryStructureName || recoilStructureName
                    : trailPath}
                </Typography>
              </Link>
            );
          })}
        {/* Only add link for beta users */}
        {isBetaUser
          ? lastPath && (
              <Link href={`/inspection/${inspectionId}/tasks`} underline="hover" color="inherit">
                <Typography color="text.primary" variant="body1">
                  {lastPath}
                </Typography>
              </Link>
            )
          : lastPath && (
              <Typography color="text.primary" variant="body1">
                {lastPath}
              </Typography>
            )}
        {selectedZone && isAtInspectionPage && (
          <Typography color="text.primary" variant="body1">
            {selectedZone?.name}
          </Typography>
        )}
      </Breadcrumbs>
      {isAtInspectionPage && isBetaUser && <ZoneStatusChanger status={selectedZone?.status} />}
    </Container>
  );
};
